import * as Sentry from '@sentry/react';
import { ValidationError } from '@/app/errors/validation-error';
import { PaymentError } from '@/app/errors/payment-error';
import envService from './env.service';

class SentryService {
  private initialized = false;

  /**
   * Determine whether a stack frame filename belongs to the UserWay widget.
   * Uses URL parsing to avoid brittle substring checks on the full URL.
   */
  private static isUserwayFrame(filename?: string | null): boolean {
    if (!filename) {
      return false;
    }

    // Only attempt URL parsing for absolute HTTP(S) URLs
    if (/^https?:\/\//i.test(filename)) {
      try {
        const url = new URL(filename);
        const host = url.hostname.toLowerCase();
        return host === 'userway.org' || host.endsWith('.userway.org');
      } catch {
        // If parsing fails, fall through to conservative false
        return false;
      }
    }

    // Non-URL filenames (e.g. relative paths) are conservatively treated as non-UserWay
    return false;
  }

  init(): void {
    const env = envService.getEnv();

    if (env.VITE_DEPLOYMENT_TARGET === 'LOCAL') {
      console.log('Sentry disabled for LOCAL environment');
      return;
    }

    if (this.initialized) {
      console.warn('Sentry already initialized');
      return;
    }

    Sentry.init({
      dsn: env.VITE_SENTRY_DSN,
      environment: env.VITE_SENTRY_ENVIRONMENT || env.VITE_DEPLOYMENT_TARGET,
      release: env.VITE_VERSION,

      // Note: Default integrations are automatically enabled and merged with custom integrations.
      // Default integrations include globalHandlersIntegration (captures error/unhandledrejection events),
      // inboundFiltersIntegration (respects ignoreErrors), breadcrumbsIntegration, and others.
      // See: https://docs.sentry.io/platforms/javascript/guides/react/configuration/integrations/
      integrations: [
        Sentry.browserTracingIntegration(),
        // Note: Session replay disabled per user preference
      ],

      // 25% sampling in production, 100% in development
      tracesSampleRate: env.VITE_DEPLOYMENT_TARGET === 'PROD' ? 0.25 : 1.0,

      // Filter expected errors
      beforeSend(event, hint) {
        const error = hint.originalException;
        if (error instanceof ValidationError || error instanceof PaymentError) {
          return null; // Don't send to Sentry
        }

        // Filter errors originating entirely from third-party scripts (no app frames)
        const frames = event.exception?.values?.[0]?.stacktrace?.frames;
        if (frames?.length && frames.every(f => SentryService.isUserwayFrame(f.filename))) {
          return null;
        }

        return event;
      },

      ignoreErrors: [
        /extensions\//i,
        /^Non-Error promise rejection captured/,
        'Network request failed',
        'Failed to fetch',
        'Network Error', // Axios XHR aborts (e.g. Instagram WKWebView cancelling requests)
        /^https?:\/\/([^/]*\.)?userway\.org\//i, // UserWay widget network failures — third-party, not actionable
        /window\.webkit\.messageHandlers/i, // Instagram/WKWebView injected scripts — not our code
        /runtime\.sendMessage/i, // Browser extension tab messaging errors — not our code
        /Event `CustomEvent`/i, // Safari wraps some rejections as CustomEvent objects — not actionable
      ],
    });

    this.initialized = true;
    console.log('Sentry initialized:', {
      environment: env.VITE_SENTRY_ENVIRONMENT,
      release: env.VITE_VERSION,
    });
  }

  setUser(userId: string): void {
    if (!this.initialized) return;
    Sentry.setUser({ id: userId });
  }

  clearUser(): void {
    if (!this.initialized) return;
    Sentry.setUser(null);
  }

  captureException(error: Error, context?: Record<string, any>): void {
    if (!this.initialized) return;
    Sentry.captureException(error, { extra: context });
  }

  captureMessage(message: string, level: 'info' | 'warning' | 'error' = 'info'): void {
    if (!this.initialized) return;
    Sentry.captureMessage(message, level);
  }

  addBreadcrumb(breadcrumb: {
    message: string;
    category?: string;
    level?: Sentry.SeverityLevel;
    data?: any;
  }): void {
    if (!this.initialized) return;
    Sentry.addBreadcrumb(breadcrumb);
  }
}

export default new SentryService();
